/**
 * Shared cross-platform referral code-extraction contract (S-14, v1).
 * See docs/specs/2026-06-06-referral-s14-design.md §7.1.
 *
 * `/r/<code>` is the v1 pre-trusted-landing floor (true deep linking is deferred,
 * §7.2). This module is the WEB implementation of the byte-identical extraction
 * contract that the iOS and Android signup slices MUST also implement:
 *   - URL shape:  https://<domain>/r/<code>
 *   - Charset:    exactly generateReferralCode's alphabet (excludes O/0/I/1/L)
 *   - Case:       uppercase-normalise on input (every surface normalises before compare)
 *   - Target:     the extracted code seeds the `referralCode` signup-draft field
 *
 * A SINGLE golden fixture (`referral-code-fixture.json`, same dir) pins the
 * expected URL -> code mapping. The web test in `referralCode.test.ts` asserts
 * against it; the iOS and Android slices assert the SAME fixture so the three
 * platforms cannot drift.
 *
 * The alphabet + length are re-exported from `@washed/core-domain` (the generator's
 * source of truth) rather than re-declared here, so the extractor and the generator
 * can never disagree about what a valid code looks like. We import from the deep
 * `@washed/core-domain/referral/code` subpath — a dependency-free, browser-safe pure
 * module — NOT the package barrel, whose server-only transitive deps (e.g.
 * @node-rs/argon2) cannot be bundled into this marketing-web browser build.
 */
import { REFERRAL_CODE_ALPHABET, REFERRAL_CODE_LENGTH } from '@washed/core-domain/referral/code';

export { REFERRAL_CODE_ALPHABET, REFERRAL_CODE_LENGTH };

/**
 * Extract the referral code from a `/r/<code>` URL (or bare path), applying the
 * §7.1 contract. Returns the uppercase-normalised code when the input is a
 * well-formed `/r/<code>` whose code is exactly REFERRAL_CODE_LENGTH characters
 * drawn from REFERRAL_CODE_ALPHABET; otherwise returns `null`.
 *
 * Pure — no I/O, no globals, deterministic. Accepts absolute URLs
 * (`https://washedafrica.com/r/AMEYO7K2`), origin-relative paths (`/r/AMEYO7K2`),
 * trailing slashes, and query/fragment suffixes. Surrounding whitespace is trimmed.
 */
export function extractReferralCodeFromUrl(input: string): string | null {
  if (typeof input !== 'string') return null;
  const trimmed = input.trim();
  if (trimmed === '') return null;

  // Reduce any absolute URL down to its pathname; treat anything else as a path.
  // We deliberately avoid the URL constructor's locale/host quirks and work on the
  // raw string so the contract is identical across platforms (Swift/Kotlin will
  // do the same string-level parse, not platform URL parsing).
  let path = trimmed;

  // Strip scheme + authority for absolute URLs (http://, https://, //host).
  const schemeMatch = path.match(/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//);
  if (schemeMatch) {
    path = path.slice(schemeMatch[0].length);
    const slash = path.indexOf('/');
    path = slash === -1 ? '' : path.slice(slash);
  } else if (path.startsWith('//')) {
    path = path.slice(2);
    const slash = path.indexOf('/');
    path = slash === -1 ? '' : path.slice(slash);
  }

  // Drop query and fragment (everything from the first '#' or '?').
  const hashAt = path.indexOf('#');
  if (hashAt !== -1) path = path.slice(0, hashAt);
  const queryAt = path.indexOf('?');
  if (queryAt !== -1) path = path.slice(0, queryAt);

  // The path must be exactly `/r/<code>` (one optional trailing slash; no further
  // segments). Capture the code segment.
  const match = path.match(/^\/r\/([^/]+)\/?$/);
  const captured = match?.[1];
  if (captured === undefined) return null;

  const code = captured.toUpperCase();
  if (!isValidReferralCode(code)) return null;
  return code;
}

/**
 * True iff `code` (already uppercase-normalised by the caller, or normalisable)
 * is a syntactically valid referral code: exactly REFERRAL_CODE_LENGTH characters,
 * every character in REFERRAL_CODE_ALPHABET. This is a SYNTAX check only — it does
 * not assert the code exists or is active (the server enforces that, §4.2).
 */
export function isValidReferralCode(code: string): boolean {
  if (typeof code !== 'string') return false;
  const upper = code.toUpperCase();
  if (upper.length !== REFERRAL_CODE_LENGTH) return false;
  for (const ch of upper) {
    if (!REFERRAL_CODE_ALPHABET.includes(ch)) return false;
  }
  return true;
}
